import { useState, type CSSProperties } from "react";
import { EntityChip } from "./EntityChip";
import { RestrictedChip } from "../scope/RestrictedChip";
import { EntityPicker } from "./EntityPicker";
import type { ResolvedRef } from "../../../lib/types";

/**
 * Sprint 12 — inline foreign-key editors for the field list.
 *
 * `ReferenceField` (type=reference) holds a single target; `ReferencesField`
 * (type=references) holds an array. Both render the chosen target(s) as
 * clickable {@link EntityChip}s with an `[x]` remove, plus an add/replace
 * affordance that opens an {@link EntityPicker} over the target type. Edits
 * call `onSave` with the new field value (string | null, or string[]).
 */

const addButtonStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  padding: "2px 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  border: "0.5px dashed var(--color-border-secondary)",
  borderRadius: "var(--kuvio-pill-radius)",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  lineHeight: 1.5,
};

const clusterStyle: CSSProperties = {
  position: "relative",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 6,
};

interface SingleProps {
  targetSlug: string;
  refs: ResolvedRef[];
  onSave: (value: string | null) => void;
  onOpenRef?: (entityTypeSlug: string, entityId: string) => void;
}

export function ReferenceField({ targetSlug, refs, onSave, onOpenRef }: SingleProps) {
  const [picking, setPicking] = useState(false);
  const current = refs[0];

  return (
    <div style={clusterStyle}>
      {current && (current.restricted ? (
        <RestrictedChip />
      ) : (
        <EntityChip
          id={current.id}
          name={current.name}
          typeSlug={current.type_slug}
          onOpen={onOpenRef}
          onRemove={() => onSave(null)}
        />
      ))}
      <button style={addButtonStyle} onClick={() => setPicking(true)}>
        <i className="ti ti-plus" style={{ fontSize: 11 }} aria-hidden="true" />
        {current ? "Change" : "Set"}
      </button>
      {picking && (
        <EntityPicker
          targetTypeSlug={targetSlug}
          onPick={(r) => {
            onSave(r.id);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

interface MultiProps {
  targetSlug: string;
  refs: ResolvedRef[];
  onSave: (value: string[]) => void;
  onOpenRef?: (entityTypeSlug: string, entityId: string) => void;
}

export function ReferencesField({ targetSlug, refs, onSave, onOpenRef }: MultiProps) {
  const [picking, setPicking] = useState(false);
  const ids = refs.map((r) => r.id);

  return (
    <div style={clusterStyle}>
      {refs.map((r, i) =>
        r.restricted ? (
          <RestrictedChip key={`restricted-${i}`} />
        ) : (
          <EntityChip
            key={r.id}
            id={r.id}
            name={r.name}
            typeSlug={r.type_slug}
            onOpen={onOpenRef}
            onRemove={() => onSave(ids.filter((x) => x !== r.id))}
          />
        ),
      )}
      <button style={addButtonStyle} onClick={() => setPicking(true)}>
        <i className="ti ti-plus" style={{ fontSize: 11 }} aria-hidden="true" />
        Add
      </button>
      {picking && (
        <EntityPicker
          targetTypeSlug={targetSlug}
          excludeIds={ids}
          onPick={(r) => {
            onSave([...ids, r.id]);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}
